import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import CommonChip from "../../../common_components/CommonChip";
import { fmtNumber, fmtCurrency, fmtPercent, getRowName, getRowKind, ROW_KIND_META } from "./shared";
const renderStyledValue = (value) => {
    if (!value || value === "-")
        return _jsx(Typography, { className: "sg-field-value", children: "\u2014" });
    const symbol = value.startsWith("$") ? "$" : value.endsWith("%") ? "%" : "";
    const raw = symbol === "$" ? value.slice(1) : symbol === "%" ? value.slice(0, -1) : value;
    return (_jsxs(Typography, { sx: { fontFamily: "Inter", fontWeight: 400, color: "#27272a", fontSize: "14px", whiteSpace: "nowrap" }, children: [symbol === "$" && _jsx(Box, { component: "span", sx: { color: "#a4a7ae", mr: 0.5 }, children: "$" }), _jsx(Box, { component: "span", sx: { color: "#27272a" }, children: raw }), symbol === "%" && _jsx(Box, { component: "span", sx: { color: "#a4a7ae", ml: 0.5 }, children: "%" })] }));
};
const AE_METRIC_FIELD = {
    T: {
        call: "call", closes: "closes", mrr: "mrr",
        conv_rate: "conv_rate", mgmt_fees: "mgmt_fees", rev_per_call: "revenue_per_call",
    },
    C: {
        call: "channel_calls", closes: "channel_closes", mrr: "channel_mrr",
        conv_rate: "channel_conv_rate", mgmt_fees: "channel_mgmt_fees", rev_per_call: "channel_revenue_per_call",
    },
    N: {
        call: "affiliate_network_calls", closes: "affiliate_network_closes", mrr: "affiliate_network_mrr",
        conv_rate: "affiliate_network_conv_rate", mgmt_fees: "affiliate_network_mgmt_fees", rev_per_call: "affiliate_network_revenue_per_call",
    },
};
const getAeMetric = (row, col) => row[AE_METRIC_FIELD[getRowKind(row)][col]];
const AE_COLUMNS = [
    { id: "name", label: "Team / NC", width: "200px", sortable: true, align: "left" },
    { id: "call", label: "Calls", width: "100px", sortable: false },
    { id: "closes", label: "Closes", width: "100px", sortable: false },
    { id: "mrr", label: "MRR", width: "120px", sortable: false },
    { id: "conv_rate", label: "Conv Rate", width: "110px", sortable: false },
    { id: "mgmt_fees", label: "Management Fees", width: "120px", sortable: false },
    { id: "rev_per_call", label: "Revenue / Call", width: "120px", sortable: false },
];
const BDR_COLUMNS = [
    { id: "name", label: "Team", width: "200px", sortable: true, align: "left" },
    { id: "enrollments", label: "Enrollments", width: "130px", sortable: false },
    { id: "outreach", label: "Outreach", width: "110px", sortable: false },
    { id: "booked", label: "Booked", width: "100px", sortable: false },
    { id: "booked_conv_rate", label: "Booked Conv Rate", width: "150px", sortable: false },
    { id: "meetings_happened", label: "Meetings Happened", width: "160px", sortable: false },
    { id: "meetings_happened_percantage", label: "Meetings Happened %", width: "170px", sortable: false },
];
export const useSalesGoalTableConfigs = (activeTab) => {
    const columns = useMemo(() => activeTab === "AE" ? AE_COLUMNS : BDR_COLUMNS, [activeTab]);
    const renderCell = (row, column) => {
        switch (column.id) {
            case "name": {
                const chip = ROW_KIND_META[getRowKind(row)];
                return (_jsxs(Box, { className: "sg-name-cell", children: [_jsx(Typography, { className: "sg-name-text all-Font-Sizes", children: getRowName(row) }), _jsx(CommonChip, { label: chip.label, tooltip: chip.tooltip, index: chip.index })] }));
            }
            // AE columns — Team rows read overall fields, NC rows read their group fields
            case "call":
                return renderStyledValue(fmtNumber(getAeMetric(row, "call")));
            case "closes":
                return renderStyledValue(fmtNumber(getAeMetric(row, "closes")));
            case "mrr":
                return renderStyledValue(fmtCurrency(getAeMetric(row, "mrr")));
            case "conv_rate":
                return renderStyledValue(fmtPercent(getAeMetric(row, "conv_rate")));
            case "mgmt_fees":
                return renderStyledValue(fmtCurrency(getAeMetric(row, "mgmt_fees")));
            case "rev_per_call":
                return renderStyledValue(fmtCurrency(getAeMetric(row, "rev_per_call")));
            // BDR columns
            case "enrollments":
                return renderStyledValue(fmtNumber(row.enrollments));
            case "outreach":
                return renderStyledValue(fmtNumber(row.outreach));
            case "booked":
                return renderStyledValue(fmtNumber(row.booked));
            case "booked_conv_rate":
                return renderStyledValue(fmtPercent(row.booked_conv_rate));
            case "meetings_happened":
                return renderStyledValue(fmtNumber(row.meetings_happened));
            case "meetings_happened_percantage":
                return renderStyledValue(fmtPercent(row.meetings_happened_percantage));
            default:
                return null;
        }
    };
    return { columns, renderCell };
};
